
import { useMemo, useEffect, useState } from 'react';
import { useAIMemory } from './useAIMemory';
import { buildRemyContext, optimizeContext } from '@/lib/ai/context';
import { supabase } from '@/lib/supabase/client';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import type { RemyContext, UserPreferences } from '@/types/ai';
import type { FusedRecommendation } from '@/services/recommendations-fusion.service';

interface UseAIContextReturn {
  context: RemyContext | null;
  isReady: boolean;
  tokenCount: number;
  refreshContext: () => void;
}

export function useAIContext(): UseAIContextReturn {
  const { user } = useUnifiedAuth();
  const { memory } = useAIMemory();
  const [userPrefs, setUserPrefs] = useState<UserPreferences | null>(null);
  const [friendRecs, setFriendRecs] = useState<FusedRecommendation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUserPrefs = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }
      
      try {
        const { data: prefsArray, error } = await supabase
          .from('user_prefs')
          .select('*')
          .eq('user_id', user.id);
        
        if (!error && prefsArray && prefsArray.length > 0) {
          const prefs = prefsArray[0];
          setUserPrefs({
            userId: prefs.user_id,
            interests: prefs.interests || [],
            pace: prefs.pace || 'standard',
            budgetMin: prefs.budget_min,
            budgetMax: prefs.budget_max,
            preferredNeighborhoods: prefs.neighborhoods || [],
            dietaryRestrictions: prefs.dietary_restrictions,
            accessibility: prefs.accessibility,
            travelStyle: prefs.travel_style,
            updatedAt: new Date(prefs.updated_at)
          });
        }
      } catch (error) {
        console.error('[useAIContext] Failed to load user prefs:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadUserPrefs();
  }, [user]);

  useEffect(() => {
    const loadFriendRecs = async () => {
      if (!user?.id || !memory) return;
      
      try {
        const savedIds = memory.context.savedVenues;
        if (savedIds.length === 0) return;
        
        const { data: venues } = await supabase
          .from('recommendations')
          .select('*')
          .in('id', savedIds)
          .not('additional_recommenders', 'is', null);
        
        if (venues) {
          const fusedRecs = venues.map(v => ({
            id: v.id,
            place_name: v.place_name,
            category: v.category,
            city: v.city,
            detected_neighborhood: v.detected_neighborhood,
            latitude: v.latitude,
            longitude: v.longitude,
            recommenders: v.additional_recommenders || [],
            friend_count: v.friend_count || 0
          } as FusedRecommendation));
          
          setFriendRecs(fusedRecs);
        }
      } catch (error) {
        console.error('[useAIContext] Failed to load friend recs:', error);
      }
    };
    
    loadFriendRecs();
  }, [user, memory?.context.savedVenues]);

  const context = useMemo(() => {
    if (!memory || isLoading) return null;
    
    const rawContext = buildRemyContext(memory, userPrefs || undefined, friendRecs);
    return optimizeContext(rawContext, 900);
  }, [memory, userPrefs, friendRecs, isLoading]);

  const tokenCount = useMemo(() => {
    if (!context) return 0;
    
    // Estimate based on context content
    const systemMessage = [
      context.summary,
      JSON.stringify(context.currentFilters),
      context.savedVenues.join(','),
      context.viewedVenues.join(',')
    ].join(' ');
    
    const messagesText = context.recentMessages
      .map(m => m.content)
      .join(' ');
    
    return Math.ceil((systemMessage.length + messagesText.length) / 4);
  }, [context]);

  const refreshContext = () => {
    setUserPrefs(null);
    setFriendRecs([]);
    setIsLoading(true);
  };

  return {
    context,
    isReady: !isLoading && context !== null,
    tokenCount,
    refreshContext
  };
}

export function useSystemMessage(): string | null {
  const { context } = useAIContext();
  
  if (!context) return null;
  
  const parts = [
    `You are Remy, an AI travel concierge for ${context.city}.`,
    `Current context: ${context.stage} stage, ${context.userContext}.`
  ];
  
  if (context.summary) {
    parts.push(`Session summary: ${context.summary}`);
  }
  
  if (context.savedVenues.length > 0) {
    parts.push(`User has ${context.savedVenues.length} saved venues.`);
  }
  
  if (context.currentFilters.pills?.length > 0) {
    parts.push(`Filtering by: ${context.currentFilters.pills.join(', ')}`);
  }
  
  return parts.join(' ');
}